import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DealContact } from '../../shared/organization-crm.service';

/**
 * Roomy compose-box for logging a CRM activity (note/call/email/meeting/cover_letter).
 * Presentation-only: emits {type, body, Contact_id, occurred_at}; the host owns the
 * POST + timeline prepend. Layout: type selector row → optional contact picker
 * (cover_letter only) → optional date + time (defaults to "now" server-side,
 * house nb-datepicker + native time input, matching submission-detail's
 * interview-date field and the timesheet tracker) → multi-line textarea →
 * Log button (right-aligned).
 * Ctrl/Cmd+Enter submits; plain Enter inserts a newline (textarea is multi-line).
 */
@Component({
  selector: 'bst-crm-activity-log',
  templateUrl: './crm-activity-log.component.html',
  styleUrls: ['./crm-activity-log.component.scss'],
  standalone: false,
})
export class CrmActivityLogComponent {
  @Input() types: string[] = ['note', 'call', 'email', 'meeting', 'cover_letter'];
  @Input() contacts: DealContact[] = [];
  @Input() saving = false;
  @Output() add = new EventEmitter<{ type: string; body: string; Contact_id?: number | null; occurred_at?: string }>();

  type = 'note';
  body = '';
  contactId: number | null = null;
  occurredDate: Date | null = null;
  occurredTime: string | null = null;

  /** Not a fixed field: a page left open across midnight must still allow today. */
  get today(): Date { return new Date(); }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      this.submit();
    }
  }

  submit(): void {
    const body = this.body.trim();
    if (!body || this.saving) { return; }
    this.add.emit({
      type: this.type,
      body,
      Contact_id: this.type === 'cover_letter' ? this.contactId : null,
      occurred_at: this.toIsoOrUndefined(),
    });
    this.body = '';
    this.contactId = null;
    this.occurredDate = null;
    this.occurredTime = null;
  }

  /**
   * Combines the date-only picker (local wall-clock date, no time) with the
   * separate HH:mm time input into one instant, then to a UTC ISO string for
   * the (UTC-configured) server. A date with no time defaults to midnight
   * local, matching how a bare occurred_at date would otherwise be read.
   */
  private toIsoOrUndefined(): string | undefined {
    if (!this.occurredDate) { return undefined; }
    const [hours, minutes] = (this.occurredTime || '00:00').split(':').map(Number);
    const d = new Date(this.occurredDate);
    d.setHours(hours || 0, minutes || 0, 0, 0);
    // Clamp to now: the date picker only blocks future DAYS ([max]="today"),
    // not a future time-of-day on today itself, and the server rejects a
    // future occurred_at outright — a rejected submit currently loses the
    // typed note with no visible error (host catch just logs). Clamping here
    // is cheap insurance against that silent loss.
    const now = Date.now();
    return d.getTime() > now ? new Date(now).toISOString() : d.toISOString();
  }
}
